import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { CRED_LABELS, formatDate, STATUS_META } from "@/lib/constants";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";
import StatusBadge from "@/components/StatusBadge";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell,
} from "recharts";

export default function Dashboard() {
  const [summary, setSummary] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [expiring, setExpiring] = useState([]);

  const load = async () => {
    try {
      const [s, t, e] = await Promise.all([
        api.get("/dashboard/summary"),
        api.get("/dashboard/expiration-timeline"),
        api.get("/credentials", { params: { days: 30 } }),
      ]);
      setSummary(s.data);
      setTimeline(t.data);
      setExpiring(e.data.slice(0, 12));
    } catch (err) {
      toast.error("Failed to load dashboard");
    }
  };

  useEffect(() => { load(); }, []);

  const runReminders = async () => {
    try {
      const r = await api.post("/reminders/run", null, { params: { dry_run: true } });
      toast.success(`Dry-run: ${r.data.count} reminders would fire`);
    } catch {
      toast.error("Reminder run failed");
    }
  };

  const bucketChart = summary ? [
    { name: "Expired", v: summary.buckets.expired, color: "#DC2626" },
    { name: "≤ 7d", v: summary.buckets.expiring_7, color: "#DC2626" },
    { name: "≤ 30d", v: summary.buckets.expiring_30, color: "#D97706" },
    { name: "≤ 60d", v: summary.buckets.expiring_60, color: "#CA8A04" },
    { name: "≤ 90d", v: summary.buckets.expiring_90, color: "#0284C7" },
    { name: "Current", v: summary.buckets.current, color: "#16A34A" },
  ] : [];

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <header className="flex flex-wrap items-end justify-between gap-4 border-b border-gray-200 pb-5">
        <div>
          <div className="overline text-gray-500">Operational Overview</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight text-slate-900 mt-1">Dashboard</h1>
          <p className="text-sm text-gray-600 mt-2">
            Louisiana ASC credentialing status, re-verification pipeline, and expiration outlook.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={runReminders}
            data-testid="dry-run-reminders-btn"
            className="flex items-center gap-2 bg-slate-900 text-white px-4 py-2 text-xs font-semibold uppercase tracking-wider hover:bg-slate-700 transition-colors"
          >
            <Sparkle size={14} weight="bold" /> Preview reminders
          </button>
        </div>
      </header>

      {/* KPI grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 border border-gray-200 bg-white">
        <Kpi label="Practitioners" value={summary?.active_practitioners ?? "—"} sub={`${summary?.total_practitioners ?? 0} total`} testid="kpi-practitioners" />
        <Kpi label="Credentials" value={summary?.total_credentials ?? "—"} sub="tracked items" testid="kpi-credentials" />
        <Kpi label="PSV Verified" value={summary?.psv_verified ?? "—"} sub={`${summary?.psv_pending ?? 0} pending`} testid="kpi-psv" color="#16A34A" />
        <Kpi label="Expired + ≤ 7d" value={(summary?.buckets?.expired ?? 0) + (summary?.buckets?.expiring_7 ?? 0)} sub="immediate action" testid="kpi-critical" color="#DC2626" />
      </div>

      {/* charts */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <section className="lg:col-span-2 bg-white border border-gray-200 p-6">
          <div className="overline text-gray-500">Status Distribution</div>
          <h2 className="font-heading text-xl font-semibold text-slate-900 mt-1 mb-5">Credential buckets</h2>
          <div className="h-[280px]">
            <ResponsiveContainer>
              <BarChart data={bucketChart} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid stroke="#E5E7EB" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#6B7280" }} tickLine={false} axisLine={{ stroke: "#E5E7EB" }} />
                <YAxis tick={{ fontSize: 11, fill: "#6B7280" }} tickLine={false} axisLine={{ stroke: "#E5E7EB" }} />
                <Tooltip cursor={{ fill: "#F3F4F6" }} contentStyle={{ border: "1px solid #0F172A", borderRadius: 0, fontSize: 12 }} />
                <Bar dataKey="v" radius={0}>
                  {bucketChart.map((e, i) => <Cell key={i} fill={e.color} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </section>
        <section className="lg:col-span-3 bg-white border border-gray-200 p-6">
          <div className="overline text-gray-500">26-week Outlook</div>
          <h2 className="font-heading text-xl font-semibold text-slate-900 mt-1 mb-5">Expiration timeline</h2>
          <div className="h-[280px]">
            <ResponsiveContainer>
              <BarChart data={timeline} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid stroke="#E5E7EB" vertical={false} />
                <XAxis dataKey="week_start" tick={{ fontSize: 10, fill: "#6B7280" }} tickLine={false} axisLine={{ stroke: "#E5E7EB" }} tickFormatter={(v) => v.slice(5)} />
                <YAxis tick={{ fontSize: 11, fill: "#6B7280" }} tickLine={false} axisLine={{ stroke: "#E5E7EB" }} />
                <Tooltip contentStyle={{ border: "1px solid #0F172A", borderRadius: 0, fontSize: 12 }} />
                <Bar dataKey="count" fill="#0F172A" radius={0} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </section>
      </div>

      {/* expiring list */}
      <section className="bg-white border border-gray-200">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <div>
            <div className="overline text-gray-500">Next Action</div>
            <h2 className="font-heading text-xl font-semibold text-slate-900 mt-0.5">Expiring in ≤ 30 days</h2>
          </div>
          <Link to="/credentials" className="text-xs uppercase tracking-wider font-semibold text-slate-700 hover:text-slate-900 flex items-center gap-1" data-testid="view-all-credentials-link">
            View all <ArrowRight size={14} />
          </Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="expiring-table">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-[0.12em] text-gray-500 border-b border-gray-200">
                <th className="px-6 py-3 font-semibold">Practitioner</th>
                <th className="px-4 py-3 font-semibold">Title</th>
                <th className="px-4 py-3 font-semibold">Credential</th>
                <th className="px-4 py-3 font-semibold">Issuer</th>
                <th className="px-4 py-3 font-semibold">Expires</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold"></th>
              </tr>
            </thead>
            <tbody>
              {expiring.length === 0 && (
                <tr><td colSpan="7" className="p-10 text-center text-gray-500 text-sm">No upcoming expirations in the next 30 days. Load demo data to see the app in action.</td></tr>
              )}
              {expiring.map((c) => (
                <tr key={c.id} className="data-row border-b border-gray-100">
                  <td className="px-6 py-3 font-medium text-slate-900">{c.practitioner?.first_name} {c.practitioner?.last_name}</td>
                  <td className="px-4 py-3 text-gray-700 font-mono text-xs">{c.practitioner?.title}</td>
                  <td className="px-4 py-3 text-slate-800">{CRED_LABELS[c.credential_type] || c.credential_type}</td>
                  <td className="px-4 py-3 text-gray-600 text-xs">{c.issuer || "—"}</td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-700">{formatDate(c.expiration_date)}</td>
                  <td className="px-4 py-3"><StatusBadge status={c.status} /></td>
                  <td className="px-4 py-3">
                    <Link to={`/practitioners/${c.practitioner_id}`} className="text-xs uppercase tracking-wider text-slate-700 font-semibold hover:text-slate-900">Open →</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function Kpi({ label, value, sub, color, testid }) {
  return (
    <div className="p-6 border-r border-b md:border-b-0 border-gray-200 last:border-r-0" data-testid={testid}>
      <div className="overline text-gray-500">{label}</div>
      <div className="mt-3 font-heading text-4xl font-bold tracking-tight" style={{ color: color || "#0F172A" }}>
        {value}
      </div>
      <div className="mt-1 text-xs text-gray-500 uppercase tracking-wide">{sub}</div>
    </div>
  );
}
